import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FormControlLabel, Radio, RadioGroup } from '@mui/material';

import AppButton from '../../../../components/designe/app-button/AppButton';
import { useStyles } from '../Sync.styles';
import theme from '../../../../styles/theme';
import { useGetExerciseProperties } from '../../../../services/redux/api/exerciseConfigAPI';
import { ExercisePropertyType } from '../../../../shared/constants';
import type {
  MuscleSyncItemDto,
  SyncMuscleRequestDto,
  SyncMuscleCreateDto,
} from '../../../../services/redux/api/syncApi';
import AppFormTextField from '../../../../components/designe/app-form-text-field';
import AppFormSelectWrapper from '../../../../components/designe/app-form-select-wrapper';
import { useGetSyncRules } from '../../../../services/redux/api/syncApi';
import { getApplySyncRuleValueForLocalPath } from '../../sync-rules/helpers/applySyncRule';

type FormMode = 'link' | 'create';

interface SyncMuscleFormProps {
  item: MuscleSyncItemDto | null;
  provider: string;
  onSave: (body: SyncMuscleRequestDto) => void;
  onDirtyChange: (dirty: boolean) => void;
  isSaving: boolean;
}

export function SyncMuscleForm({
  item,
  provider,
  onSave,
  onDirtyChange,
  isSaving,
}: SyncMuscleFormProps) {
  const { t } = useTranslation();
  const { classes } = useStyles(theme);
  const { data: exerciseProperties = [] } = useGetExerciseProperties();

  const [mode, setMode] = useState<FormMode>('link');
  const [localResourceId, setLocalResourceId] = useState<string>('');
  const [createNameKey, setCreateNameKey] = useState('');
  const [createPriority, setCreatePriority] = useState<number>(0);
  const [createParentId, setCreateParentId] = useState<string>('');

  const { data: syncRules = [] } = useGetSyncRules();

  useEffect(() => {
    if (!item) return;
    const synced = Boolean(item.localResourceId);
    setMode(synced ? 'link' : 'create');
    setLocalResourceId(item.localResourceId ?? '');
    if (synced) {
      setCreateNameKey('');
      setCreatePriority(0);
      setCreateParentId('');
    } else {
      const nameVal = getApplySyncRuleValueForLocalPath({
        item,
        rules: syncRules,
        provider,
        entity: 'muscle',
        localPath: 'nameKey',
      });
      const priorityVal = getApplySyncRuleValueForLocalPath({
        item,
        rules: syncRules,
        provider,
        entity: 'muscle',
        localPath: 'priority',
      });
      setCreateNameKey(nameVal);
      setCreatePriority(priorityVal ? Number(priorityVal) : 0);
      setCreateParentId('');
    }
  }, [item?.providerResourceId, provider, syncRules]);

  const muscles = useMemo(
    () =>
      exerciseProperties.filter((p) => p.type === ExercisePropertyType.muscle),
    [exerciseProperties],
  );

  const parentOptions = useMemo(
    () =>
      muscles.map((m) => ({
        id: m.id,
        name: m.nameKey,
      })),
    [muscles],
  );

  const linkOptions = useMemo(
    () =>
      muscles.map((m) => ({
        id: m.id,
        label: [m.nameKey, m.name].filter(Boolean).join(' — ') || m.id,
      })),
    [muscles],
  );

  const canSave = useMemo(() => {
    if (!item) return false;
    if (mode === 'link') {
      const value = localResourceId?.trim();
      if (!value) return false;
      // disable when nothing changed
      return value !== (item.localResourceId ?? '');
    }
    if (mode === 'create') return Boolean(createNameKey?.trim());
    return false;
  }, [item, mode, localResourceId, createNameKey]);

  const hasUnsavedChanges = useMemo(() => {
    if (!item) return false;
    if (mode === 'link')
      return localResourceId !== (item.localResourceId ?? '');
    return (
      Boolean(createNameKey?.trim()) ||
      Boolean(createParentId) ||
      createPriority !== 0
    );
  }, [
    item,
    mode,
    localResourceId,
    createNameKey,
    createParentId,
    createPriority,
  ]);

  useEffect(() => {
    if (!item) return;
    if (mode !== 'create') return;
    const nameVal = getApplySyncRuleValueForLocalPath({
      item,
      rules: syncRules,
      provider,
      entity: 'muscle',
      localPath: 'nameKey',
    });
    const priorityVal = getApplySyncRuleValueForLocalPath({
      item,
      rules: syncRules,
      provider,
      entity: 'muscle',
      localPath: 'priority',
    });
    setCreateNameKey(nameVal);
    setCreatePriority(priorityVal ? Number(priorityVal) : 0);
  }, [item?.providerResourceId, mode, provider, syncRules]);

  useEffect(() => {
    onDirtyChange(hasUnsavedChanges);
  }, [hasUnsavedChanges, onDirtyChange]);

  const formik = {
    values: {
      createNameKey,
      createPriority,
      createParentId,
      localResourceId,
    },
    initialValues: {
      createNameKey: '',
      createPriority: 0,
      createParentId: '',
      localResourceId: '',
    },
    touched: {},
    errors: {},
    handleBlur: () => {},
    setFieldValue: (name: string, value: any) => {
      switch (name) {
        case 'createNameKey':
          setCreateNameKey(value);
          break;
        case 'createPriority':
          setCreatePriority(value);
          break;
        case 'createParentId':
          setCreateParentId(value);
          break;
        case 'localResourceId':
          setLocalResourceId(value);
          break;
        default:
          break;
      }
    },
  };

  const handleSave = () => {
    if (!item || !canSave) return;
    if (mode === 'link' && localResourceId) {
      onSave({
        providerResourceId: item.providerResourceId,
        localResourceId,
      });
    }
    if (mode === 'create' && createNameKey) {
      const create: SyncMuscleCreateDto = {
        nameKey: createNameKey.trim(),
        type: ExercisePropertyType.muscle,
        priority: createPriority,
      };
      if (createParentId) create.parentId = createParentId;
      onSave({
        providerResourceId: item.providerResourceId,
        create,
      });
    }
  };

  if (!item) {
    return (
      <div className={classes.formColumn}>
        <div className={classes.panelTitle}>
          {t('adminDashboard.sync.formTitle')}
        </div>
        <div className={classes.formContent}>
          {t('adminDashboard.sync.selectItem')}
        </div>
      </div>
    );
  }

  return (
    <div className={classes.formColumn}>
      <div className={classes.panelTitle}>
        {t('adminDashboard.sync.formTitle')}
      </div>
      <div className={classes.formContent}>
        <RadioGroup
          row
          value={mode}
          onChange={(_, v) => setMode(v as FormMode)}>
          <FormControlLabel
            value="link"
            control={<Radio size="small" />}
            label={t('adminDashboard.sync.linkExisting')}
          />
          <FormControlLabel
            value="create"
            control={<Radio size="small" />}
            label={t('adminDashboard.sync.createNew')}
          />
        </RadioGroup>

        {mode === 'link' && (
          <AppFormSelectWrapper
            label={t('adminDashboard.sync.selectMuscle')}
            name="localResourceId"
            options={linkOptions}
            fieldName="label"
            disableClear
            formik={formik}
            onChange={(e) => {
              const val = e?.target?.value;
              setLocalResourceId(val?.id ?? '');
              formik.setFieldValue('localResourceId', val?.id ?? '');
            }}
          />
        )}

        {mode === 'create' && (
          <>
            <AppFormTextField
              label={t('common.nameKey')}
              fullWidth
              name="createNameKey"
              formik={formik}
            />
            <AppFormSelectWrapper
              label={t('adminDashboard.parent')}
              name="createParentId"
              options={parentOptions}
              disableClear
              formik={formik}
              onChange={(e) => {
                const val = e?.target?.value;
                const id = val?.id ?? '';
                setCreateParentId(id);
                formik.setFieldValue('createParentId', id);
              }}
            />
            <AppFormTextField
              label={t('adminDashboard.sync.priority')}
              fullWidth
              name="createPriority"
              type="number"
              formik={formik}
            />
          </>
        )}

        <AppButton
          variant="contained"
          onClick={handleSave}
          disabled={!canSave}
          loader={isSaving}>
          {t('common.save')}
        </AppButton>
      </div>
    </div>
  );
}
